import type { MaybeRefOrGetter } from 'vue'
import type { ComprehendLanguage, LanguageDetectionInput } from '~/models/LangaugeDetector'
import type { TTSLanguage } from '~/models/Brikke/TextToSpeech'
import { computed, toValue } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { stripHtml } from '~/utils/dom'
import { useComprehendApi } from '~/api/comprehendApi'

const MAX_LENGTH = 200
const SCORE_MIN = 0.45
const CHUNK_SIZE = 25 // hard limit in Comprehend

export default (
  input: MaybeRefOrGetter<Array<LanguageDetectionInput>>
) => {
  const { batchDetectLanguage } = useComprehendApi()

  const textRef = computed(() =>
    toValue(input)
      .flatMap(i => i.text)
      .map(text => stripHtml(text).slice(0, MAX_LENGTH))
  )

  const { data: detection, isLoading } = useQuery({
    enabled: computed(() => textRef.value.length > 0),
    queryKey: ['language-detector', textRef],
    queryFn: async () => {
      const texts = textRef.value
      const chunks: Array<{ chunk: string[], startIndex: number }> = []

      for (let i = 0; i < texts.length; i += CHUNK_SIZE) {
        chunks.push({
          chunk: texts.slice(i, i + CHUNK_SIZE),
          startIndex: i,
        })
      }

      const chunkResults = await Promise.all(
        chunks.map(({ chunk }) => batchDetectLanguage(chunk))
      )

      return chunkResults.flatMap((chunkResult, chunkIndex) =>
        chunkResult.map(item => ({
          ...item,
          Index: item.Index + chunks[chunkIndex].startIndex,
        }))
      )
    },
    staleTime: Infinity,
    retry: 1,
  })

  const result = computed(() => {
    const detectionData = detection.value
    if (!detectionData) {
      return new Map<number, TTSLanguage | null>()
    }

    const inputArray = toValue(input)
    const resultMap = new Map<number, TTSLanguage | null>()

    inputArray.forEach((item) => {
      const textArray = item.text
      const languages: ComprehendLanguage[] = []

      textArray.forEach((_, textIndex) => {
        const globalIndex = inputArray
          .slice(0, inputArray.indexOf(item))
          .reduce((sum, prev) => sum + prev.text.length, 0) + textIndex

        const detectionResult = detectionData[globalIndex]
        if (!detectionResult?.Languages || detectionResult.Languages.length === 0) {
          return
        }
        const topLanguage = detectionResult.Languages[0]
        if (topLanguage.Score < SCORE_MIN) {
          return
        }
        languages.push(topLanguage.LanguageCode)
      })

      if (languages.length === 0) {
        resultMap.set(item.contentId, null)
        return
      }

      const langCount = languages.reduce((acc, lang) => {
        acc[lang] = (acc[lang] || 0) + 1
        return acc
      }, {} as Record<ComprehendLanguage, number>)

      const dominantLang = Object.entries(langCount)
        .sort(([, a], [, b]) => b - a)[0][0] as ComprehendLanguage

      resultMap.set(item.contentId, mapToTTSLanguage(dominantLang))
    })

    return resultMap
  })

  function mapToTTSLanguage(langCode: ComprehendLanguage): TTSLanguage | null {
    const mapping: Record<ComprehendLanguage, TTSLanguage> = {
      no: 'nb_NO',
      da: 'nb_NO',
      sv: 'nb_NO',
      en: 'en_GB',
      fr: 'fr_FR',
      de: 'de_DE',
      es: 'es_ES',
    }
    return mapping[langCode] || null
  }

  return {
    result,
    isLoading,
  }
}
